/**
 * Builds styled QuotesCustomerPortalExportXL.xlsx matching legacy portal My Quotes summary.
 */

import { downloadXlsxBytes, zipStore } from 'ish-shared/utils/presidio-ooxml-zip';

import { QuotingHelper } from '../models/quoting/quoting.helper';
import { QuotingEntity } from '../models/quoting/quoting.model';

const CONTENT_TYPES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  <Override PartName="/xl/sharedStrings.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml"/>
</Types>`;

const RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

const WORKBOOK_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" Target="sharedStrings.xml"/>
</Relationships>`;

const WORKBOOK = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="Page 1" sheetId="1" r:id="rId1"/>
  </sheets>
</workbook>`;

/** Exact styles from legacy QuotesCustomerPortalExportXL. */
const STYLES = `<?xml version="1.0" encoding="utf-8"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <numFmts count="1">
    <numFmt numFmtId="164" formatCode="&quot;$&quot;0.00"/>
  </numFmts>
  <fonts count="5">
    <font><sz val="10"/><color rgb="FF000000"/><name val="Arial"/><charset val="1"/></font>
    <font><b/><sz val="16"/><color rgb="FF000000"/><name val="Arial"/><charset val="1"/></font>
    <font><b/><sz val="8"/><color rgb="FF000000"/><name val="Arial"/><charset val="1"/></font>
    <font><sz val="8"/><color rgb="FF000000"/><name val="Arial"/><charset val="1"/></font>
    <font><b/><sz val="8"/><color rgb="FFFFFFFF"/><name val="Arial"/><charset val="1"/></font>
  </fonts>
  <fills count="4">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="00FFFFFF"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF000000"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="8">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1"><alignment horizontal="left"/></xf>
    <xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="left" vertical="top"/></xf>
    <xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="left" vertical="top"/></xf>
    <xf numFmtId="0" fontId="3" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="left" vertical="top"/></xf>
    <xf numFmtId="0" fontId="4" fillId="3" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="left" vertical="top"/></xf>
    <xf numFmtId="0" fontId="4" fillId="3" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="left" vertical="top" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="4" fillId="3" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="top"/></xf>
    <xf numFmtId="164" fontId="3" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1" applyAlignment="1"><alignment horizontal="right" vertical="top"/></xf>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
  <dxfs count="0"/>
  <tableStyles count="0" defaultTableStyle="TableStyleMedium9" defaultPivotStyle="PivotStyleLight16"/>
</styleSheet>`;

const S = {
  title: 1,
  labelBold: 2,
  body: 3,
  header: 4,
  headerWrap: 5,
  headerCenter: 6,
  money: 7,
} as const;

export const QUOTES_SUMMARY_EXPORT_HEADERS = [
  'Quote #',
  'Quote Date',
  'Title',
  'Affiliated Company',
  'Quote Contact',
  'Status',
  'Grand Total',
] as const;

const COL_WIDTHS = [
  14.2755003072, 17.390154919680004, 28.68077788992, 18.29859584832, 19.466591328000003, 15.31371851136,
  17.909264021760002, 113.94444790656001,
];

function xmlEscape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatPortalDateTime(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const h24 = date.getHours();
  const h12 = h24 % 12 || 12;
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()} ${h12}:${pad(date.getMinutes())}:${pad(
    date.getSeconds()
  )} ${ampm}`;
}

/** Fields needed for the My Quotes summary export (list stub or detail). */
interface QuoteSummarySource {
  number?: string;
  displayName?: string;
  creationDate?: number;
  affiliatedCompany?: string;
  contact?: string;
  listTotal?: number;
  total?: { value?: number };
}

function asSummarySource(quote: QuotingEntity): QuoteSummarySource {
  return quote as QuoteSummarySource;
}

export function quoteSummaryGrandTotal(quote: QuotingEntity): number {
  const e = asSummarySource(quote);
  const fromDetail = e.total?.value;
  if (typeof fromDetail === 'number' && Number.isFinite(fromDetail)) {
    return fromDetail;
  }
  const fromList = e.listTotal;
  return typeof fromList === 'number' && Number.isFinite(fromList) ? fromList : 0;
}

export function formatQuoteSummaryDate(creationDate: number | undefined): string {
  if (creationDate === undefined || creationDate === null || Number.isNaN(Number(creationDate))) {
    return '';
  }
  return formatPortalDateTime(new Date(Number(creationDate)));
}

export interface QuotesSummaryExportRow {
  quoteNo: string;
  quoteDate: string;
  title: string;
  affiliatedCompany: string;
  contact: string;
  status: string;
  grandTotal: number;
}

export function buildQuotesSummaryRows(quotes: QuotingEntity[]): QuotesSummaryExportRow[] {
  return (quotes || []).map(q => {
    const e = asSummarySource(q);
    return {
      quoteNo: e.number || '',
      quoteDate: formatQuoteSummaryDate(e.creationDate),
      title: e.displayName || '',
      affiliatedCompany: e.affiliatedCompany || '',
      contact: e.contact || '',
      status: QuotingHelper.state(q),
      grandTotal: quoteSummaryGrandTotal(q),
    };
  });
}

class SharedStrings {
  private readonly map = new Map<string, number>();
  private readonly list: string[] = [];

  index(value: string): number {
    const existing = this.map.get(value);
    if (existing !== undefined) {
      return existing;
    }
    const idx = this.list.length;
    this.map.set(value, idx);
    this.list.push(value);
    return idx;
  }

  toXml(): string {
    const items = this.list.map(s => `<si><t>${xmlEscape(s)}</t></si>`).join('');
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="${this.list.length}" uniqueCount="${this.list.length}">${items}</sst>`;
  }
}

function sharedCell(ref: string, style: number, sstIndex: number): string {
  return `<c r="${ref}" s="${style}" t="s"><v>${sstIndex}</v></c>`;
}

function numberCell(ref: string, style: number, value: number): string {
  return `<c r="${ref}" s="${style}"><v>${value}</v></c>`;
}

function emptyStyled(ref: string, style: number): string {
  return `<c r="${ref}" s="${style}"/>`;
}

export interface QuotesSummaryXlsxParts {
  stylesXml: string;
  sheetXml: string;
  sharedStringsXml: string;
}

export function buildQuotesSummaryXlsxParts(
  quotes: QuotingEntity[],
  exportedAt: Date = new Date()
): QuotesSummaryXlsxParts {
  const rows = buildQuotesSummaryRows(quotes);
  const sst = new SharedStrings();
  const rowsXml: string[] = [];

  rowsXml.push(
    `<row r="1" spans="1:8" ht="22.5432" customHeight="1">${sharedCell(
      'A1',
      S.title,
      sst.index('My Quotes')
    )}${emptyStyled('B1', S.title)}</row>`
  );
  rowsXml.push(
    `<row r="2" spans="1:8" ht="11.6352" customHeight="1">${sharedCell(
      'A2',
      S.labelBold,
      sst.index('Date:')
    )}${sharedCell('B2', S.body, sst.index(formatPortalDateTime(exportedAt)))}</row>`
  );

  const headerStyles = [S.header, S.headerWrap, S.header, S.header, S.header, S.header, S.headerCenter];
  const headerCells = QUOTES_SUMMARY_EXPORT_HEADERS.map((label, i) =>
    sharedCell(`${String.fromCharCode(65 + i)}3`, headerStyles[i], sst.index(label))
  ).join('');
  rowsXml.push(`<row r="3" spans="1:8" ht="11.6352" customHeight="1">${headerCells}</row>`);

  let total = 0;
  rows.forEach((row, i) => {
    const r = 4 + i;
    const ht = i === 1 ? '10.908' : '11.6352';
    total += row.grandTotal;
    const cells = [
      sharedCell(`A${r}`, S.body, sst.index(row.quoteNo)),
      sharedCell(`B${r}`, S.body, sst.index(row.quoteDate)),
      sharedCell(`C${r}`, S.body, sst.index(row.title)),
      sharedCell(`D${r}`, S.body, sst.index(row.affiliatedCompany)),
      sharedCell(`E${r}`, S.body, sst.index(row.contact)),
      sharedCell(`F${r}`, S.body, sst.index(row.status)),
      numberCell(`G${r}`, S.money, row.grandTotal),
    ].join('');
    rowsXml.push(`<row r="${r}" spans="1:8" ht="${ht}" customHeight="1">${cells}</row>`);
  });

  const footerRow = 4 + rows.length;
  const rounded = Math.round(total * 100) / 100;
  rowsXml.push(
    `<row r="${footerRow}" spans="1:8" ht="11.6352" customHeight="1">${sharedCell(
      `F${footerRow}`,
      S.labelBold,
      sst.index('Total:')
    )}${numberCell(`G${footerRow}`, S.money, rounded)}</row>`
  );

  const colsXml = COL_WIDTHS.map(
    (w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`
  ).join('');

  const sheetXml = `<?xml version="1.0" encoding="utf-8"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>
  <dimension ref="A1:H${footerRow}"/>
  <sheetViews><sheetView workbookViewId="0"><selection activeCell="A1" sqref="A1"/></sheetView></sheetViews>
  <sheetFormatPr defaultRowHeight="15"/>
  <cols>${colsXml}</cols>
  <sheetData>${rowsXml.join('')}</sheetData>
  <mergeCells count="1"><mergeCell ref="A1:B1"/></mergeCells>
  <pageMargins left="0.3937007874015748" right="0.3937007874015748" top="0.3937007874015748" bottom="0.3937007874015748" header="0" footer="0"/>
  <pageSetup paperSize="0" orientation="portrait" fitToHeight="0"/>
</worksheet>`;

  return {
    stylesXml: STYLES,
    sheetXml,
    sharedStringsXml: sst.toXml(),
  };
}

export function buildQuotesSummaryXlsxBytes(
  quotes: QuotingEntity[],
  exportedAt: Date = new Date()
): Uint8Array {
  const parts = buildQuotesSummaryXlsxParts(quotes, exportedAt);
  return zipStore({
    '[Content_Types].xml': CONTENT_TYPES,
    '_rels/.rels': RELS,
    'xl/workbook.xml': WORKBOOK,
    'xl/_rels/workbook.xml.rels': WORKBOOK_RELS,
    'xl/styles.xml': parts.stylesXml,
    'xl/sharedStrings.xml': parts.sharedStringsXml,
    'xl/worksheets/sheet1.xml': parts.sheetXml,
  });
}

/** MVP 3.1.1 — legacy QuotesCustomerPortalExportXL look. */
export function downloadQuotesSummaryXlsx(quotes: QuotingEntity[], exportedAt: Date = new Date()): void {
  const bytes = buildQuotesSummaryXlsxBytes(quotes, exportedAt);
  downloadXlsxBytes(bytes, 'QuotesCustomerPortalExportXL.xlsx');
}
